import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import MarkdownText from '../components/MarkdownText';

const emptyForm = {
  slug: '',
  title: '',
  summary: '',
  body_md: '',
  cover_url: '',
  status: 'draft',
  skip_telegram: false,
  skip_max: false,
  skip_vk: false,
};

const CHANNEL_LABELS = {
  telegram: 'Telegram',
  max: 'MAX',
  vk: 'ВКонтакте',
  dzen: 'Дзен (RSS)',
};

function CopyBlock({ label, value }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="admin-articles-export">
      <div className="admin-articles-export-head">
        <strong>{label}</strong>
        <button type="button" className="btn btn-outline btn-sm" onClick={() => void onCopy()}>
          {copied ? 'Скопировано' : 'Копировать'}
        </button>
      </div>
      <textarea className="admin-articles-export-text" readOnly rows={6} value={value || ''} />
    </div>
  );
}

export default function AdminArticlesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedSlug = searchParams.get('slug') || '';

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [exports, setExports] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [publishMsg, setPublishMsg] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [isNew, setIsNew] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/articles/manage/');
      setList(Array.isArray(res.data) ? res.data : []);
    } catch {
      setError('Не удалось загрузить статьи.');
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const loadArticle = useCallback(async (slug) => {
    if (!slug) return;
    setFormError('');
    setPublishMsg('');
    try {
      const res = await api.get(`/articles/manage/${encodeURIComponent(slug)}/`);
      const a = res.data;
      setForm({
        slug: a.slug || '',
        title: a.title || '',
        summary: a.summary || '',
        body_md: a.body_md || '',
        cover_url: a.cover_url || '',
        status: a.status || 'draft',
        skip_telegram: Boolean(a.skip_telegram),
        skip_max: Boolean(a.skip_max),
        skip_vk: Boolean(a.skip_vk),
      });
      setExports(a.exports || null);
      setDeliveries(a.deliveries || []);
      setIsNew(false);
    } catch {
      setFormError('Не удалось открыть статью.');
    }
  }, []);

  useEffect(() => {
    if (selectedSlug) {
      void loadArticle(selectedSlug);
    }
  }, [selectedSlug, loadArticle]);

  const selectSlug = (slug) => {
    if (!slug) {
      setSearchParams({});
      return;
    }
    setSearchParams({ slug });
  };

  const startNew = () => {
    setIsNew(true);
    setForm(emptyForm);
    setExports(null);
    setDeliveries([]);
    setFormError('');
    setPublishMsg('');
    setSearchParams({});
  };

  const onField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    setSaving(true);
    setFormError('');
    setPublishMsg('');
    try {
      const payload = { ...form };
      if (!payload.slug?.trim()) delete payload.slug;
      let res;
      if (isNew || !selectedSlug) {
        res = await api.post('/articles/manage/', payload);
      } else {
        res = await api.patch(`/articles/manage/${encodeURIComponent(selectedSlug)}/`, payload);
      }
      const a = res.data;
      setExports(a.exports || null);
      setDeliveries(a.deliveries || []);
      setIsNew(false);
      await fetchList();
      selectSlug(a.slug);
      setPublishMsg('Сохранено.');
      return a.slug;
    } catch (e) {
      const detail = e?.response?.data;
      setFormError(
        typeof detail === 'string'
          ? detail
          : detail?.detail || detail?.title?.[0] || 'Не удалось сохранить.',
      );
      return null;
    } finally {
      setSaving(false);
    }
  };

  const refreshExports = async () => {
    if (!selectedSlug) return;
    try {
      const res = await api.get(`/articles/manage/${encodeURIComponent(selectedSlug)}/exports/`);
      setExports(res.data);
    } catch {
      setFormError('Не удалось обновить заготовки.');
    }
  };

  const publishChannels = async (update = false) => {
    setPublishing(true);
    setPublishMsg('');
    setFormError('');
    try {
      const slug = (await save()) || selectedSlug;
      if (!slug) return;
      const res = await api.post(
        `/articles/manage/${encodeURIComponent(slug)}/publish-channels/`,
        { update },
      );
      setDeliveries(res.data?.deliveries || res.data?.article?.deliveries || []);
      if (res.data?.article) {
        setExports(res.data.article.exports || null);
      }
      setPublishMsg(update ? 'Обновление на площадках завершено.' : 'Публикация на площадки завершена.');
      await fetchList();
    } catch (e) {
      setFormError(e?.response?.data?.detail || 'Ошибка публикации на площадки.');
    } finally {
      setPublishing(false);
    }
  };

  const remove = async () => {
    if (!selectedSlug || !window.confirm('Удалить статью безвозвратно?')) return;
    try {
      await api.delete(`/articles/manage/${encodeURIComponent(selectedSlug)}/`);
      startNew();
      await fetchList();
    } catch {
      setFormError('Не удалось удалить.');
    }
  };

  const statusLabel = useMemo(
    () => (form.status === 'published' ? 'Опубликовано' : 'Черновик'),
    [form.status],
  );

  return (
    <div className="admin-articles-page">
      <div className="page-header">
        <div>
          <h1>Статьи</h1>
          <p className="text-secondary">
            Пишете здесь один раз. Заготовки для Дзена и мессенджеров собираются автоматически.
            RSS для Дзена: <code>/rss/dzen.xml</code>
          </p>
        </div>
        <Link to="/articles" className="btn btn-outline btn-sm">
          Публичный список
        </Link>
      </div>

      {error && <p className="form-error">{error}</p>}

      <div className="admin-articles-layout">
        <aside className="admin-articles-sidebar">
          <button type="button" className="btn btn-primary btn-sm" onClick={startNew}>
            Новая статья
          </button>
          {loading ? (
            <p className="text-secondary">Загрузка…</p>
          ) : (
            <ul className="admin-articles-list">
              {list.map((a) => (
                <li key={a.slug}>
                  <button
                    type="button"
                    className={
                      a.slug === selectedSlug
                        ? 'admin-articles-list-btn is-active'
                        : 'admin-articles-list-btn'
                    }
                    onClick={() => selectSlug(a.slug)}
                  >
                    <span>{a.title}</span>
                    <span className="text-secondary">
                      {a.status === 'published' ? 'опубл.' : 'черновик'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <div className="admin-articles-editor">
          {(selectedSlug || isNew) && (
            <>
              <div className="admin-articles-form">
                <label>
                  Заголовок
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => onField('title', e.target.value)}
                  />
                </label>
                <label>
                  Slug (URL)
                  <input
                    type="text"
                    value={form.slug}
                    onChange={(e) => onField('slug', e.target.value)}
                    placeholder="сгенерируется из заголовка"
                  />
                </label>
                <label>
                  Краткий зачин (для ВК / карточек)
                  <textarea
                    rows={3}
                    value={form.summary}
                    onChange={(e) => onField('summary', e.target.value)}
                  />
                </label>
                <label>
                  Текст (Markdown)
                  <textarea
                    rows={18}
                    className="admin-articles-body"
                    value={form.body_md}
                    onChange={(e) => onField('body_md', e.target.value)}
                  />
                </label>
                <label>
                  URL обложки
                  <input
                    type="url"
                    value={form.cover_url}
                    onChange={(e) => onField('cover_url', e.target.value)}
                    placeholder="https://… (для Дзена ≥700px)"
                  />
                </label>

                <div className="admin-articles-row">
                  <label>
                    Статус
                    <select
                      value={form.status}
                      onChange={(e) => onField('status', e.target.value)}
                    >
                      <option value="draft">Черновик</option>
                      <option value="published">Опубликовано на сайте</option>
                    </select>
                  </label>
                  <span className="text-secondary">Сейчас: {statusLabel}</span>
                </div>

                <fieldset className="admin-articles-skips">
                  <legend>Не слать на площадку</legend>
                  <label>
                    <input
                      type="checkbox"
                      checked={form.skip_telegram}
                      onChange={(e) => onField('skip_telegram', e.target.checked)}
                    />
                    Telegram
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={form.skip_max}
                      onChange={(e) => onField('skip_max', e.target.checked)}
                    />
                    MAX
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={form.skip_vk}
                      onChange={(e) => onField('skip_vk', e.target.checked)}
                    />
                    ВК
                  </label>
                </fieldset>

                {formError && <p className="form-error">{formError}</p>}
                {publishMsg && <p className="text-secondary">{publishMsg}</p>}

                <div className="admin-articles-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={saving || !form.title.trim() || !form.body_md.trim()}
                    onClick={() => void save()}
                  >
                    {saving ? 'Сохраняю…' : 'Сохранить'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => setShowPreview((v) => !v)}
                  >
                    {showPreview ? 'Скрыть превью' : 'Превью сайта'}
                  </button>
                  {selectedSlug && (
                    <>
                      <button
                        type="button"
                        className="btn btn-outline"
                        disabled={publishing}
                        onClick={() => void publishChannels(false)}
                      >
                        {publishing ? 'Шлю…' : 'Опубликовать на площадки'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline"
                        disabled={publishing}
                        onClick={() => void publishChannels(true)}
                      >
                        Обновить на площадках
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={() => void refreshExports()}
                      >
                        Обновить заготовки
                      </button>
                      <Link
                        to={`/articles/${encodeURIComponent(selectedSlug)}`}
                        className="btn btn-outline btn-sm"
                      >
                        Открыть
                      </Link>
                      <button type="button" className="btn btn-danger btn-sm" onClick={() => void remove()}>
                        Удалить
                      </button>
                    </>
                  )}
                </div>
              </div>

              {showPreview && (
                <div className="admin-articles-preview">
                  <h2>Превью</h2>
                  <MarkdownText text={form.body_md || ''} />
                </div>
              )}

              {exports && (
                <div className="admin-articles-exports">
                  <h2>Заготовки для площадок</h2>
                  {exports.has_code && (
                    <p className="text-secondary">
                      В статье есть код — на Дзене и в мессенджерах вместо блоков будет ссылка на сайт.
                    </p>
                  )}
                  <CopyBlock label="Карточка ВК / короткий анонс" value={exports.messenger_card} />
                  <CopyBlock label="Telegram (полный до лимита)" value={exports.telegram_full} />
                  <CopyBlock label="MAX (полный до лимита)" value={exports.max_full} />
                  <CopyBlock label="Дзен HTML (без кода)" value={exports.dzen_html} />
                  <p className="text-secondary">
                    Ссылка на сайте: {exports.site_url}
                  </p>
                </div>
              )}

              {deliveries?.length > 0 && (
                <div className="admin-articles-deliveries">
                  <h2>Журнал доставки</h2>
                  <ul>
                    {deliveries.map((d) => (
                      <li key={d.channel}>
                        <strong>{CHANNEL_LABELS[d.channel] || d.channel}</strong>
                        {': '}
                        {d.status}
                        {d.external_id ? ` · id ${d.external_id}` : ''}
                        {d.last_error ? ` · ${d.last_error}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {!selectedSlug && !isNew && (
            <p className="text-secondary">Выберите статью слева или создайте новую.</p>
          )}
        </div>
      </div>
    </div>
  );
}
